/* ==========================================================================
   MYPITZ — Landing page behaviour
   --------------------------------------------------------------------------
   Vanilla, dependency-free, idempotent. Every initialiser is safe to call
   again, which is what lets the whole thing survive Shopify theme-editor
   section reloads (see the shopify:section:load listener at the bottom).
   ========================================================================== */

(function () {
  'use strict';

  function each(root, selector, fn) {
    Array.prototype.forEach.call((root || document).querySelectorAll(selector), fn);
  }

  // Per-initialiser "already wired" flag. This has to be keyed per feature, not
  // shared: the FAQ container carries both `mp-reveal` and `data-mp-faq`, so a
  // single shared flag let the reveal pass claim it and the accordion pass skip
  // the element entirely — clicks did nothing.
  function claim(el, feature) {
    var key = 'mp' + feature;
    if (el.dataset[key]) return false;
    el.dataset[key] = '1';
    return true;
  }

  /* --- Reveal on scroll --- */

  var revealObserver = null;

  function getRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;
    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    return revealObserver;
  }

  function initReveal(root) {
    var observer = getRevealObserver();
    each(root, '.mp-reveal', function (el) {
      if (!claim(el, 'Reveal')) return;
      if (!observer) {
        el.classList.add('is-in');
        return;
      }
      observer.observe(el);
    });
  }

  /* --- FAQ accordion --- */

  function initFaq(root) {
    each(root, '[data-mp-faq]', function (list) {
      if (!claim(list, 'Faq')) return;

      var single = list.dataset.mpFaqSingle === 'true';

      list.addEventListener('click', function (event) {
        var trigger = event.target.closest('.mp-faq__q');
        if (!trigger || !list.contains(trigger)) return;

        var item = trigger.closest('.mp-faq__item');
        var willOpen = !item.classList.contains('is-open');

        if (single && willOpen) {
          each(list, '.mp-faq__item.is-open', function (other) {
            other.classList.remove('is-open');
            other.querySelector('.mp-faq__q').setAttribute('aria-expanded', 'false');
          });
        }

        item.classList.toggle('is-open', willOpen);
        trigger.setAttribute('aria-expanded', String(willOpen));
      });
    });
  }

  /* --- Video lightbox --- */

  var lightbox = null;

  function buildLightbox() {
    if (lightbox) return lightbox;

    lightbox = document.createElement('div');
    lightbox.className = 'mp-lb';
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.setAttribute('aria-label', 'נגן וידאו');
    lightbox.innerHTML =
      '<button class="mp-lb__close" type="button" aria-label="סגירה">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">' +
      '<path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
      '<div class="mp-lb__frame"></div>';

    lightbox.addEventListener('click', function (event) {
      if (event.target === lightbox || event.target.closest('.mp-lb__close')) {
        closeLightbox();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeLightbox();
    });

    document.body.appendChild(lightbox);
    return lightbox;
  }

  function openLightbox(html, ratio) {
    var box = buildLightbox();
    box.style.setProperty('--mp-lb-ratio', ratio || '16 / 9');
    box.querySelector('.mp-lb__frame').innerHTML = html;
    box.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    box.querySelector('.mp-lb__close').focus();
  }

  function closeLightbox() {
    if (!lightbox || !lightbox.classList.contains('is-open')) return;
    lightbox.classList.remove('is-open');
    // Clearing the frame is what actually stops playback (both <video> and iframes).
    lightbox.querySelector('.mp-lb__frame').innerHTML = '';
    document.body.style.overflow = '';
  }

  function embedUrl(raw) {
    var url = String(raw || '').trim();
    var youtube = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/
    );
    if (youtube) {
      return 'https://www.youtube.com/embed/' + youtube[1] + '?autoplay=1&rel=0&playsinline=1';
    }
    var vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeo) {
      return 'https://player.vimeo.com/video/' + vimeo[1] + '?autoplay=1';
    }
    return null;
  }

  function videoTag(src) {
    return '<video src="' + src + '" controls autoplay playsinline style="object-fit:contain"></video>';
  }

  function iframeTag(src) {
    return '<iframe src="' + src + '" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>';
  }

  function initLightboxTriggers(root) {
    each(root, '[data-mp-lightbox]', function (el) {
      if (!claim(el, 'Lightbox')) return;

      el.setAttribute('role', 'button');
      el.setAttribute('tabindex', '0');

      function open() {
        var ratio = el.dataset.mpRatio || '16 / 9';
        var file = el.dataset.mpVideoFile;
        var src = el.dataset.mpLightbox;

        if (file) {
          openLightbox(videoTag(file), ratio);
          return;
        }

        var embed = embedUrl(src);
        if (embed) {
          openLightbox(iframeTag(embed), ratio);
        } else if (src) {
          window.open(src, '_blank', 'noopener');
        }
      }

      el.addEventListener('click', open);
      el.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
      });
    });
  }

  /* --- Sticky CTA bar --- */

  function initSticky(root) {
    each(root, '[data-mp-sticky]', function (bar) {
      if (!claim(bar, 'Sticky')) return;

      var after = parseInt(bar.dataset.mpStickyAfter || '600', 10);
      // Hide the bar again once the lead form is on screen — no point nagging
      // someone who is already looking at the thing you want them to fill in.
      var target = document.querySelector('#mypitz-form, [data-mp-sticky-hide]');
      var formVisible = false;

      if (target && 'IntersectionObserver' in window) {
        new IntersectionObserver(
          function (entries) {
            formVisible = entries[0].isIntersecting;
            update();
          },
          { threshold: 0.15 }
        ).observe(target);
      }

      function update() {
        var show = window.scrollY > after && !formVisible;
        bar.classList.toggle('is-visible', show);
        document.body.classList.toggle('mp-has-sticky', show);
      }

      var ticking = false;
      window.addEventListener(
        'scroll',
        function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(function () {
            update();
            ticking = false;
          });
        },
        { passive: true }
      );

      update();
    });
  }

  /* --- Smooth anchor scrolling --- */

  function initAnchors(root) {
    each(root, 'a[href^="#mypitz-"]', function (link) {
      if (!claim(link, 'Anchor')) return;

      link.addEventListener('click', function (event) {
        var target = document.querySelector(link.getAttribute('href'));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        var field = target.querySelector('input:not([type="hidden"]), select, textarea');
        if (field) {
          window.setTimeout(function () {
            field.focus({ preventScroll: true });
          }, 620);
        }
      });
    });
  }

  /* --- Upload dropzone --- */

  function initDropzone(root) {
    each(root, '[data-mp-drop]', function (zone) {
      if (!claim(zone, 'Drop')) return;

      var input = zone.querySelector('input[type="file"]');
      var readout = zone.querySelector('[data-mp-drop-count]');
      if (!input) return;

      function describe(count) {
        if (!readout) return;
        if (!count) {
          readout.textContent = '';
        } else if (count === 1) {
          readout.textContent = 'נבחרה תמונה אחת ✓';
        } else {
          readout.textContent = 'נבחרו ' + count + ' תמונות ✓';
        }
      }

      zone.addEventListener('click', function (event) {
        if (event.target !== input) input.click();
      });

      ['dragenter', 'dragover'].forEach(function (name) {
        zone.addEventListener(name, function (event) {
          event.preventDefault();
          zone.classList.add('is-over');
        });
      });

      ['dragleave', 'drop'].forEach(function (name) {
        zone.addEventListener(name, function (event) {
          event.preventDefault();
          zone.classList.remove('is-over');
        });
      });

      zone.addEventListener('drop', function (event) {
        if (!event.dataTransfer || !event.dataTransfer.files.length) return;
        try {
          input.files = event.dataTransfer.files;
        } catch (error) {
          /* Some browsers disallow assigning FileList; the picker still works. */
        }
        describe(input.files.length);
      });

      input.addEventListener('change', function () {
        describe(input.files.length);
      });
    });
  }

  /* --- Lead form --- */

  // Normalises Israeli numbers to E.164 so WhatsApp deep links always resolve.
  function toIntlPhone(raw) {
    var digits = String(raw || '').replace(/[^\d+]/g, '');
    if (digits.indexOf('+') === 0) return digits.slice(1);
    if (digits.indexOf('00') === 0) return digits.slice(2);
    if (digits.indexOf('0') === 0) return '972' + digits.slice(1);
    return digits;
  }

  function initForm(root) {
    each(root, '[data-mp-form]', function (form) {
      if (!claim(form, 'Form')) return;

      form.addEventListener('submit', function (event) {
        var phone = form.querySelector('[data-mp-phone]');
        if (phone && phone.value.replace(/\D/g, '').length < 9) {
          event.preventDefault();
          phone.setCustomValidity('נא להזין מספר טלפון תקין');
          phone.reportValidity();
          return;
        }
        if (phone) phone.setCustomValidity('');

        var button = form.querySelector('[type="submit"]');
        if (button) {
          button.dataset.mpLabel = button.textContent;
          button.textContent = 'שולח…';
          button.disabled = true;
          // Re-enable on bfcache restore so a back-navigation isn't stuck.
          window.setTimeout(function () {
            button.disabled = false;
            button.textContent = button.dataset.mpLabel;
          }, 8000);
        }
      });

      // Pre-fill a WhatsApp message from whatever the visitor already typed.
      each(form, '[data-mp-wa-link]', function (link) {
        link.addEventListener('click', function () {
          var name = form.querySelector('[data-mp-collect="שם"]');
          var city = form.querySelector('[data-mp-collect="עיר"]');
          var base = link.dataset.mpWaText || 'היי, אשמח לפרטים על סרטון תדמית לנכס';
          var parts = [base];
          if (name && name.value) parts.push('שמי ' + name.value);
          if (city && city.value) parts.push('הנכס ב' + city.value);
          link.href =
            'https://wa.me/' +
            toIntlPhone(link.dataset.mpWaPhone) +
            '?text=' +
            encodeURIComponent(parts.join('. '));
        });
      });
    });
  }

  /* --- Post-submit --- */
  // Shopify's contact form does a full round-trip, so on the way back the
  // confirmation is somewhere down the page. Bring it into view once.

  function focusSuccess() {
    var note = document.querySelector('.mp-form__msg--ok');
    if (!note || !claim(note, 'Success')) return;
    note.scrollIntoView({ behavior: 'smooth', block: 'center' });
    note.focus({ preventScroll: true });
  }

  /* --- Boot --- */

  function init(root) {
    initReveal(root);
    initFaq(root);
    initLightboxTriggers(root);
    initSticky(root);
    initAnchors(root);
    initDropzone(root);
    initForm(root);
  }

  function boot() {
    init(document);
    focusSuccess();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-run for the section that was just re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function () {
    document.body.classList.remove('mp-has-sticky');
  });

  window.MyPitz = { init: init, openLightbox: openLightbox, toIntlPhone: toIntlPhone };
})();